import { useEffect, useMemo, useRef } from "react";

const ptsStr = (poly) =>
  (poly || []).map((p) => `${(p[0] * 100).toFixed(2)},${(p[1] * 100).toFixed(2)}`).join(" ");

const borner = (v, mini, maxi) => Math.min(maxi, Math.max(mini, v));

/* Curseur du mode « désigner un bâtiment » : un cercle barré d'une croix, tracé
   en blanc sur un liseré sombre. Sur une photo satellite — vert foncé, toits
   gris, ombres — un curseur d'une seule couleur disparaît la moitié du temps.
   Le point chaud est au centre du cercle, pas en haut à gauche : c'est le
   centre qui désigne le bâtiment. */
const CURSEUR_VISEE = (() => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
<g fill="none" stroke="#111827" stroke-width="4" stroke-linecap="round">
<circle cx="14" cy="14" r="9"/><path d="M10 10l8 8M18 10l-8 8"/></g>
<g fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round">
<circle cx="14" cy="14" r="9"/><path d="M10 10l8 8M18 10l-8 8"/></g></svg>`;
  return `url("data:image/svg+xml;base64,${btoa(svg)}") 14 14, crosshair`;
})();

/** Rayons des poignees, proportionnels a la taille du batiment trace.
 *
 * Avec des rayons fixes, un cabanon disparaissait sous ses propres poignees :
 * le cercle d'ajout (r=3) et la zone de saisie du sommet (r=4,5) couvraient
 * toute la toiture, rendant le trace impossible. On mesure donc le cote le plus
 * long du polygone (le repere va de 0 a 100) et on en deduit les rayons, bornes
 * pour rester saisissables au doigt sur un petit toit et ne pas grossir
 * au-dela des valeurs d'origine sur un grand.
 */
/* Un doigt couvre ~7 mm là où un pointeur de souris vise au pixel : sur écran
   tactile, les zones de saisie doivent être plus généreuses. On ne grossit QUE
   les rayons de SAISIE (cercles transparents) — jamais le dessin, dont la
   taille réduite est une exigence : des poignées visibles plus grosses
   recouvraient entièrement un cabanon. Les sommets étant peints après les
   milieux, ils gagnent les chevauchements, comme à la souris. */
const TACTILE = typeof window !== "undefined" &&
  window.matchMedia && window.matchMedia("(pointer: coarse)").matches;

function rayonsPoignees(polygon) {
  const miniSommet = TACTILE ? 3.2 : 1.7;
  const miniMilieu = TACTILE ? 2.0 : 1.0;
  if (!polygon || polygon.length < 2) return { sommet: 4.5, milieu: 3, point: 1.4 };
  const xs = polygon.map((p) => p[0]);
  const ys = polygon.map((p) => p[1]);
  const cote = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) * 100;
  return {
    sommet: borner(cote * 0.09, miniSommet, 4.5),
    milieu: borner(cote * 0.055, miniMilieu, 3),
    point: borner(cote * 0.028, 0.55, 1.4),
  };
}

/**
 * `guides` : repères Sat Premium. Ce sont les rectangles que Google renvoie
 * (bâtiment mesuré, puis chaque pan de toit) — la seule géométrie que Solar
 * expose. Ils ne se saisissent pas et ne comptent dans aucun calcul : ils
 * répondent à une question, « est-ce bien la maison de mon client qui a été
 * mesurée ? ». Sans eux, une mesure du bâtiment voisin est indétectable.
 * Chaque guide : { boite: [[x,y]...], principal: bool }.
 */
export default function RoofMap({ image, polygon, editable = false, onPolygonChange, extraPolygons = [], guides = [], onReleverPoint, viser = false, className = "" }) {
  const containerRef = useRef(null);
  const dragIdx = useRef(null);
  const bodyDrag = useRef(null);
  // Triple tape = supprimer un sommet au tactile (l'appui long sert a viser
  // avant de glisser, il ne doit jamais supprimer). Le double-clic et le clic
  // droit restent les gestes de suppression a la souris.
  const tapes = useRef({ i: null, n: 0, t: 0 });
  const polyRef = useRef(polygon);
  const cbRef = useRef(onPolygonChange);
  polyRef.current = polygon;
  cbRef.current = onPolygonChange;

  useEffect(() => {
    if (!editable) return;
    const clamp = (v) => Math.min(1, Math.max(0, v));
    const rect = () => containerRef.current.getBoundingClientRect();
    const move = (e) => {
      if (dragIdx.current !== null) {
        e.preventDefault();
        const r = rect();
        const x = clamp((e.clientX - r.left) / r.width);
        const y = clamp((e.clientY - r.top) / r.height);
        const next = (polyRef.current || []).map((p, i) => (i === dragIdx.current ? [x, y] : p));
        cbRef.current && cbRef.current(next);
      } else if (bodyDrag.current) {
        e.preventDefault();
        const r = rect();
        const { sx, sy, poly } = bodyDrag.current;
        const dx = (e.clientX - sx) / r.width;
        const dy = (e.clientY - sy) / r.height;
        const next = poly.map(([x, y]) => [clamp(x + dx), clamp(y + dy)]);
        cbRef.current && cbRef.current(next);
      }
    };
    const up = () => { dragIdx.current = null; bodyDrag.current = null; };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [editable]);

  const startBodyDrag = (e) => {
    e.preventDefault();
    bodyDrag.current = { sx: e.clientX, sy: e.clientY, poly: (polyRef.current || []).map((p) => [...p]) };
  };

  const insertMidpoint = (i) => {
    const poly = polyRef.current || [];
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const next = [...poly.slice(0, i + 1), mid, ...poly.slice(i + 1)];
    cbRef.current && cbRef.current(next);
    dragIdx.current = i + 1;
  };

  const removeVertex = (i) => {
    const poly = polyRef.current || [];
    if (poly.length <= 3) return;
    cbRef.current && cbRef.current(poly.filter((_, idx) => idx !== i));
  };

  const rayon = useMemo(() => rayonsPoignees(polygon), [polygon]);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden rounded-md border border-line bg-field select-none ${className}`}
      data-testid="roof-map"
      style={{
        touchAction: editable ? "none" : "auto",
        // iOS : l'appui long sur la photo ouvrait le menu « Enregistrer
        // l'image » en plein tracé.
        WebkitTouchCallout: "none",
        ...(onReleverPoint ? { cursor: viser ? CURSEUR_VISEE : "crosshair" } : {}),
      }}
    >
      {image ? (
        /* Clic sur l'image = relever les coordonnées du point visé. Le contour
           et ses poignées interceptent les leurs (ils sont peints par-dessus),
           donc tracer reste possible : seul un clic sur la photo elle-même
           relève un point. */
        <img
          src={image}
          alt="Satellite roof view"
          draggable={false}
          className="w-full h-full object-cover"
          onClick={onReleverPoint ? (e) => {
            const r = containerRef.current.getBoundingClientRect();
            onReleverPoint((e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
          } : undefined}
        />
      ) : (
        <div className="w-full aspect-square flex items-center justify-center text-ink-faint text-sm">No image</div>
      )}
      {/* ⚠️ `pointerEvents: none` sur la RACINE du calque, sinon il recouvre
          l'image et intercepte tout : le clic n'atteint jamais la photo et le
          curseur reste celui du SVG. Les enfants qui doivent être saisissables
          (le contour, ses poignées) le remettent à « all » chacun de leur côté. */}
      <svg viewBox="0 0 100 100" preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full" style={{ pointerEvents: "none" }}>
        {/* Repères sous les contours : ils informent, ils ne se manipulent pas. */}
        {guides.map((g, gi) => (
          <polygon
            key={`g${gi}`}
            points={ptsStr(g.boite)}
            fill="none"
            stroke={g.principal ? "#22D3EE" : "#67E8F9"}
            strokeWidth={g.principal ? 0.55 : 0.3}
            strokeDasharray={g.principal ? "2 1.2" : "1 1"}
            strokeLinejoin="round"
            data-testid={`guide-solar-${gi}`}
            style={{ pointerEvents: "none" }}
          />
        ))}
        {extraPolygons.map((ep, ei) =>
          ep.polygon && ep.polygon.length >= 3 ? (
            <polygon key={`x${ei}`} points={ptsStr(ep.polygon)} fill="rgba(234,179,8,0.18)" stroke="#EAB308" strokeWidth="0.6" strokeLinejoin="round" style={{ pointerEvents: "none" }} />
          ) : null
        )}
        {polygon && polygon.length >= 3 && (
          <polygon
            points={ptsStr(polygon)}
            fill="rgba(234,88,12,0.30)"
            stroke="#EA580C"
            strokeWidth="0.7"
            strokeLinejoin="round"
            data-testid="roof-body"
            style={{ pointerEvents: editable ? "all" : "none", cursor: editable ? "move" : "default" }}
            onPointerDown={editable ? startBodyDrag : undefined}
          />
        )}

        {editable && polygon && polygon.map((p, i) => {
          const b = polygon[(i + 1) % polygon.length];
          const mid = [(p[0] + b[0]) / 2, (p[1] + b[1]) / 2];
          return (
            <circle
              key={`m${i}`}
              cx={mid[0] * 100}
              cy={mid[1] * 100}
              r={rayon.milieu}
              fill="rgba(255,255,255,0.25)"
              stroke="#EA580C"
              strokeWidth={rayon.milieu * 0.1}
              data-testid={`roof-midpoint-${i}`}
              style={{ cursor: "copy", pointerEvents: "all" }}
              onPointerDown={(e) => { e.preventDefault(); insertMidpoint(i); }}
            />
          );
        })}

        {editable && polygon && polygon.map((p, i) => (
          <g key={`v${i}`}>
            <circle
              cx={p[0] * 100}
              cy={p[1] * 100}
              r={rayon.sommet}
              fill="transparent"
              data-testid={`roof-vertex-${i}`}
              style={{ cursor: "grab", pointerEvents: "all" }}
              onPointerDown={(e) => {
                e.preventDefault();
                dragIdx.current = i;
                if (e.pointerType !== "mouse") {
                  // Trois tapes rapprochees sur le meme sommet = suppression.
                  const now = Date.now();
                  const t = tapes.current;
                  if (t.i === i && now - t.t < 600) t.n += 1;
                  else { t.i = i; t.n = 1; }
                  t.t = now;
                  if (t.n >= 3) {
                    tapes.current = { i: null, n: 0, t: 0 };
                    dragIdx.current = null;
                    removeVertex(i);
                  }
                }
              }}
              onDoubleClick={(e) => { e.preventDefault(); removeVertex(i); }}
              onContextMenu={(e) => { e.preventDefault(); removeVertex(i); }}
            />
            <circle cx={p[0] * 100} cy={p[1] * 100} r={rayon.point} fill="#EA580C" stroke="#fff" strokeWidth={rayon.point * 0.22} style={{ pointerEvents: "none" }} />
          </g>
        ))}

        {!editable && polygon && polygon.map((p, i) => (
          <circle key={`d${i}`} cx={p[0] * 100} cy={p[1] * 100} r="0.9" fill="#EA580C" stroke="#fff" strokeWidth="0.3" style={{ pointerEvents: "none" }} />
        ))}
      </svg>
    </div>
  );
}
